import { Link } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  ShieldCheck,
  Stamp,
  FileSearch2,
  Scale,
  FileSignature,
  ClipboardCheck,
  Lock,
  CalendarClock,
  Banknote,
  Users,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileCheck2,
} from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { PageSEO } from "@/components/site/PageSEO";
import { CTASection } from "@/components/site/CTASection";
import { QuickAnswer } from "@/components/site/QuickAnswer";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Reveal, RevealStagger, RevealItem } from "@/components/motion/Reveal";
import { stockImages } from "@/data/site/stockImages";
import { brand, founder } from "@/data/site/brand";

const path = "/how-it-works";

const steps = [
  {
    icon: Phone,
    n: "01",
    eyebrow: "Day 1 · Free",
    title: "Free, confidential consultation",
    body:
      "Call us or submit a request. A credentialed CPA — not a salesperson, not an intake rep — reads every IRS notice you've received, the deadlines running against you, and what's actually at stake. The call is protected by IRC §7216 confidentiality from the first hello. No script, no pressure, no upsell.",
    deliverables: [
      "Same-day callback in most cases (M–F)",
      "Honest read on your case — including when we're not the right fit",
      "Written flat-fee estimate before you engage the firm",
    ],
    youReceive: "A clear yes/no on whether resolution makes sense, and a written fee quote.",
  },
  {
    icon: Stamp,
    n: "02",
    eyebrow: "Days 1–3 · Engagement",
    title: "Power of Attorney filed — IRS contact stops",
    body:
      "The moment you sign the engagement letter, we file IRS Form 2848 (Power of Attorney). From that point forward the IRS contacts us, not you. We also lodge Form 8821 where helpful so we can pull every transcript on file — account, wage-and-income, return, and record-of-account — for the last 10 years.",
    deliverables: [
      "Form 2848 filed within 24 business hours",
      "Active wage garnishments and bank levies addressed first",
      "Full transcript pull (10-year lookback) + CSED statute analysis",
    ],
    youReceive: "A confirmation that we're now your authorized representative on record with the IRS.",
  },
  {
    icon: FileSearch2,
    n: "03",
    eyebrow: "Weeks 1–3 · Investigation",
    title: "Compliance check and case analysis",
    body:
      "Resolution programs require compliance first — all required returns filed, current-year withholding or estimated payments on track. We map every unfiled year, reconcile what the IRS already has on wage-and-income transcripts, and run the financial analysis (Form 433-A/B/F) that determines which programs you actually qualify for.",
    deliverables: [
      "Unfiled-return inventory + filing plan (often 1–6 returns)",
      "433-A / 433-B financial analysis vs. IRS Collection Financial Standards",
      "Statute-of-limitations (CSED) map for every assessment",
    ],
    youReceive: "A written case summary: balances, statutes, compliance gaps, and your real options.",
  },
  {
    icon: Scale,
    n: "04",
    eyebrow: "Months 1–6 · Strategy",
    title: "Resolution strategy + IRS negotiation",
    body:
      "We build the resolution that fits your facts — Offer in Compromise, installment agreement, Partial-Pay IA, Currently-Not-Collectible status, penalty abatement, audit defense, Trust Fund Recovery Penalty defense, innocent-spouse relief, or Collection Due Process Appeal. Then we negotiate directly with Revenue Officers, Appeals Officers, and the OIC unit — in writing, on the record.",
    deliverables: [
      "All IRS phone calls, faxes, and notices routed through the firm",
      "Direct CPA access — never handed off to a paralegal",
      "Material-update notifications whenever your case moves",
    ],
    youReceive: "A signed resolution proposal filed with the IRS and a realistic timeline to acceptance.",
  },
  {
    icon: ClipboardCheck,
    n: "05",
    eyebrow: "Closing · Forward",
    title: "Resolution closed + forward-compliance plan",
    body:
      "We close the case, document the resolution in writing, and confirm the IRS has posted the result on your account transcript. Then we hand you a forward-compliance plan — quarterly estimates, payroll deposit cadence, withholding adjustments, return-filing reminders — built specifically for your situation so the same problem doesn't return.",
    deliverables: [
      "Written closing memo with IRS posting confirmation",
      "Forward-compliance calendar tailored to your facts",
      "Open door — we're here if a future notice arrives",
    ],
    youReceive: "A clean IRS account, a paid-or-resolved status, and a plan to stay out of trouble.",
  },
];

const compareRows: { feature: string; ctr: string; mill: string }[] = [
  { feature: "Who answers your first call", ctr: "A credentialed CPA", mill: "Commissioned sales rep" },
  { feature: "Who works your case", ctr: "Same CPA, start to finish", mill: "Rotating 'case manager' team" },
  { feature: "Fee structure", ctr: "Flat fee, written scope", mill: "Tiered retainers, surprise add-ons" },
  { feature: "Cases declined when not winnable", ctr: "Yes — we say so up front", mill: "Almost never — fee comes first" },
  { feature: "IRS POA (Form 2848) filed in", ctr: "24 business hours", mill: "Often 30+ days" },
  { feature: "Direct phone access to your preparer", ctr: "Yes", mill: "Routed through call center" },
];

const whatWeFile = [
  { form: "Form 2848", purpose: "Power of Attorney — IRS contacts the firm, not you", when: "Day 1" },
  { form: "Form 8821", purpose: "Tax Information Authorization — full transcript access", when: "Day 1" },
  { form: "Form 433-A / 433-B / 433-F", purpose: "Collection information statement (individual / business / streamlined)", when: "Weeks 1–3" },
  { form: "Form 656", purpose: "Offer in Compromise", when: "When OIC is the fit" },
  { form: "Form 9465", purpose: "Installment Agreement request", when: "When IA is the fit" },
  { form: "Form 843", purpose: "Penalty abatement claim (reasonable cause / FTA)", when: "Where penalties apply" },
  { form: "Form 12153", purpose: "Request for Collection Due Process Hearing", when: "If levy/lien is threatened" },
  { form: "Form 8857", purpose: "Innocent Spouse Relief", when: "Where applicable" },
];

const guarantees = [
  {
    icon: Lock,
    title: "Confidential from the first call",
    body: "IRC §7216 protects everything you share — even on a free consult, even if you don't hire us.",
  },
  {
    icon: Banknote,
    title: "Flat-fee engagements",
    body: "Written scope, written price. No hourly creep, no surprise add-ons mid-case.",
  },
  {
    icon: Users,
    title: "No commissioned sales team",
    body: "You speak with a CPA on day one. We don't pay closers to push you into a retainer.",
  },
  {
    icon: ShieldCheck,
    title: "We turn cases away",
    body: "If you'd be better off on the IRS payment portal or with a different specialist, we say so.",
  },
];

const faqs = [
  {
    q: "How long does the full process take?",
    a: "Most cases close in 4–9 months. Streamlined installment agreements can resolve in 30–60 days. Offers in Compromise typically run 6–12 months because the IRS unit is backlogged — but the active negotiation on our side usually wraps in weeks, not months.",
  },
  {
    q: "Will the IRS keep calling me once you're engaged?",
    a: "No. The moment Form 2848 is on file, the IRS is required to route contact through us. If a Revenue Officer keeps reaching out to you directly, you forward it to us and we handle it.",
  },
  {
    q: "What does it actually cost?",
    a: "Engagements are flat-fee and scoped in writing before you commit. Simple installment-agreement cases start in the low four figures. Complex multi-year Offer in Compromise or audit-defense engagements price higher because they involve more filings and more IRS negotiation hours. You get the exact number — in writing — before you sign anything.",
  },
  {
    q: "What if I have unfiled returns?",
    a: "Almost every case starts with unfiled years. Filing compliance is a prerequisite for every IRS resolution program, so we build return-preparation into the engagement and file them in the correct order to minimize new penalties.",
  },
  {
    q: "What if I can't pay anything right now?",
    a: "That's exactly the case for Currently-Not-Collectible (CNC) status. We document your financials against IRS Collection Financial Standards, file for CNC, and the IRS halts active collection. Penalties and interest still accrue, but levies and garnishments stop.",
  },
];

const howToLd = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: `How ${brand.shortName} resolves your IRS tax problem`,
  description:
    "A five-step CPA-led process — free consultation, Power of Attorney, investigation, negotiation, and closing — used to resolve IRS controversies for individuals and businesses.",
  totalTime: "P180D",
  estimatedCost: { "@type": "MonetaryAmount", currency: "USD", value: "0" },
  step: steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.title,
    text: s.body,
  })),
};

const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const HowItWorks = () => (
  <SiteLayout>
    <PageSEO
      title="How It Works — Our 5-Step IRS Resolution Process"
      description="The exact 5-step process a CPA-led firm uses to resolve IRS problems — free consultation, Power of Attorney, investigation, negotiation, and closing. Timeline, forms filed, and what to expect."
      path={path}
      image={stockImages.consultation.src}
      breadcrumbs={[
        { name: "Home", href: "/" },
        { name: "How It Works", href: path },
      ]}
      jsonLd={[howToLd, faqLd]}
    />

    {/* HERO */}
    <section className="relative bg-gradient-navy text-white overflow-hidden">
      <HeroBackground image={stockImages.consultation} priority />
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14 lg:py-24 max-w-6xl">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 items-center">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-4">
              Our process · 5 steps
            </p>
            <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 [text-wrap:balance]">
              From first call to <span className="text-gold">closed IRS case</span> — exactly how it goes.
            </h1>
            <p className="text-lg text-white/85 max-w-2xl leading-relaxed mb-7">
              No mystery, no salesperson, no marketing layer. A credentialed CPA reads your notices,
              files Power of Attorney, runs the IRS analysis, and negotiates your resolution in writing.
              Here's the whole workflow — start to finish.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href={`tel:${brand.phoneTel}`}
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-6 py-3 rounded transition-transform hover:scale-[1.03]"
              >
                <Phone className="w-5 h-5" /> Call {brand.phone}
              </a>
              <Link
                to="/free-consultation"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white font-semibold px-6 py-3 rounded border border-white/20 backdrop-blur"
              >
                Request a free consult <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>

          <Reveal>
            <div className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-6 lg:p-7">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-4">
                At a glance
              </div>
              <dl className="space-y-4">
                {[
                  { icon: CalendarClock, label: "Typical timeline", value: "4–9 months end-to-end" },
                  { icon: Stamp, label: "POA filed within", value: "24 business hours" },
                  { icon: FileCheck2, label: "Transcript lookback", value: "10 years pulled" },
                  { icon: Banknote, label: "Fees", value: "Flat-fee, written upfront" },
                ].map((row) => (
                  <div key={row.label} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-md bg-gold/15 text-gold flex items-center justify-center shrink-0">
                      <row.icon className="w-4 h-4" />
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-[0.15em] text-white/55 font-semibold">
                        {row.label}
                      </dt>
                      <dd className="text-base font-semibold text-white">{row.value}</dd>
                    </div>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* QUICK ANSWER */}
    <section className="py-12 lg:py-16 bg-white">
      <div className="container mx-auto px-4 max-w-4xl">
        <QuickAnswer
          eyebrow="The short version"
          heading="How it works, in one paragraph"
          answer="You call us. A CPA — not a sales rep — reads your IRS notices and gives you a flat-fee quote. If you engage the firm, we file Form 2848 within 24 hours so the IRS stops contacting you and starts contacting us. We pull 10 years of IRS transcripts, run the financial analysis, file the right resolution program (OIC, installment agreement, CNC, penalty abatement, audit defense), and close the case with written confirmation on your IRS account."
          bullets={[
            "Step 1: free consult with a credentialed CPA",
            "Step 2: Form 2848 filed — IRS contact routes to us",
            "Step 3: 10-year transcript pull + compliance check",
            "Step 4: program filed + negotiation in writing",
            "Step 5: closing memo + forward-compliance plan",
          ]}
        />
      </div>
    </section>

    {/* TIMELINE */}
    <section className="py-16 lg:py-24 bg-secondary/40 border-y border-border">
      <div className="container mx-auto px-4 max-w-6xl">
        <SectionHeading
          eyebrow="Timeline"
          title="What happens, and when"
          intro="Real engagements vary, but this is the rhythm a typical case follows. The first three steps run in the first month; steps four and five carry the case to resolution."
        />
        <div className="relative">
          {/* spine */}
          <div className="hidden md:block absolute left-[34px] top-3 bottom-3 w-px bg-gradient-to-b from-gold/0 via-gold/40 to-gold/0" aria-hidden="true" />
          <RevealStagger className="space-y-10 lg:space-y-14" stagger={80}>
            {steps.map((step) => (
              <RevealItem key={step.n}>
                <article className="group grid md:grid-cols-[auto,1fr] gap-6 md:gap-8 items-start">
                  <div className="relative flex md:block items-center gap-4">
                    <div className="relative w-14 h-14 rounded-lg bg-secondary text-navy flex items-center justify-center shrink-0 group-hover:bg-gold group-hover:text-navy-dark transition-colors">
                      <step.icon className="w-6 h-6" strokeWidth={1.75} />
                    </div>
                    <span className="md:hidden font-heading text-2xl font-bold text-navy/30">{step.n}</span>
                  </div>
                  <div className="bg-white rounded-xl border border-border/70 p-6 lg:p-8 shadow-sm">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                      <span className="font-heading text-xl font-bold text-navy/20">{step.n}</span>
                      <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">
                        {step.eyebrow}
                      </span>
                    </div>
                    <h3 className="font-heading text-2xl md:text-[1.7rem] font-bold text-navy mb-3 leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-foreground/85 leading-[1.75] text-[16px] mb-5">{step.body}</p>
                    <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mb-5">
                      {step.deliverables.map((d) => (
                        <li key={d} className="flex gap-2.5 text-[14px] text-foreground/85">
                          <CheckCircle2 className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="border-t border-border/60 pt-4 flex items-start gap-3 text-sm">
                      <FileSignature className="w-4 h-4 text-navy shrink-0 mt-0.5" />
                      <div>
                        <span className="font-bold text-navy">You receive: </span>
                        <span className="text-foreground/80">{step.youReceive}</span>
                      </div>
                    </div>
                  </div>
                </article>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </div>
    </section>

    {/* WHAT WE FILE */}
    <section className="py-16 lg:py-24 bg-white">
      <div className="container mx-auto px-4 max-w-5xl">
        <SectionHeading
          icon={FileCheck2}
          eyebrow="Paperwork"
          title="The IRS forms we file on your behalf"
          intro="Every resolution program runs on specific IRS forms. Here's the working set — filed in the right order, at the right moment in your case."
        />
        <Reveal>
          <div className="overflow-hidden rounded-xl border border-border shadow-sm">
            <table className="w-full text-left">
              <thead className="bg-navy text-white">
                <tr>
                  <th className="px-4 lg:px-6 py-3 text-[11px] font-bold uppercase tracking-[0.15em]">Form</th>
                  <th className="px-4 lg:px-6 py-3 text-[11px] font-bold uppercase tracking-[0.15em]">Purpose</th>
                  <th className="px-4 lg:px-6 py-3 text-[11px] font-bold uppercase tracking-[0.15em] hidden md:table-cell">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {whatWeFile.map((row) => (
                  <tr key={row.form} className="hover:bg-secondary/40 transition-colors">
                    <td className="px-4 lg:px-6 py-4 font-semibold text-navy whitespace-nowrap">{row.form}</td>
                    <td className="px-4 lg:px-6 py-4 text-foreground/85">{row.purpose}</td>
                    <td className="px-4 lg:px-6 py-4 text-sm text-muted-foreground hidden md:table-cell whitespace-nowrap">{row.when}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Full reference library:{" "}
            <Link to="/resources/irs-forms" className="text-navy font-semibold underline-offset-2 hover:underline">
              All IRS forms we work with →
            </Link>
          </p>
        </Reveal>
      </div>
    </section>

    {/* COMPARE */}
    <section className="py-16 lg:py-24 bg-navy text-white">
      <div className="container mx-auto px-4 max-w-5xl">
        <Reveal>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-3">
            CPA firm vs. tax-relief mill
          </p>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold leading-tight mb-3 [text-wrap:balance]">
            Why the workflow looks different here.
          </h2>
          <p className="text-white/75 max-w-2xl mb-8">
            The "tax relief" industry is a marketing machine layered on top of a small handful of actual
            tax practitioners. We're the practitioner — no layer.
          </p>
        </Reveal>
        <Reveal>
          <div className="overflow-hidden rounded-xl border border-white/15">
            <table className="w-full text-left">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-4 lg:px-6 py-3 text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">What you compare</th>
                  <th className="px-4 lg:px-6 py-3 text-[11px] font-bold uppercase tracking-[0.15em] text-gold">{brand.shortName}</th>
                  <th className="px-4 lg:px-6 py-3 text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">Typical "tax relief" company</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {compareRows.map((r) => (
                  <tr key={r.feature}>
                    <td className="px-4 lg:px-6 py-4 font-semibold text-white/90 align-top">{r.feature}</td>
                    <td className="px-4 lg:px-6 py-4 align-top">
                      <div className="flex gap-2 text-white">
                        <CheckCircle2 className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                        <span>{r.ctr}</span>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 align-top">
                      <div className="flex gap-2 text-white/70">
                        <XCircle className="w-4 h-4 text-white/40 mt-0.5 shrink-0" />
                        <span>{r.mill}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>

    {/* GUARANTEES */}
    <section className="py-16 lg:py-24 bg-secondary/40 border-y border-border">
      <div className="container mx-auto px-4 max-w-6xl">
        <SectionHeading
          eyebrow="What we guarantee"
          title="Four commitments — in writing."
          intro="These aren't bullet points on a brochure. They show up on the engagement letter you sign."
        />
        <RevealStagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-5" stagger={70}>
          {guarantees.map((g) => (
            <RevealItem key={g.title}>
              <div className="group h-full bg-white rounded-xl border border-border p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-lg bg-secondary text-navy flex items-center justify-center mb-4 group-hover:bg-gold group-hover:text-navy-dark transition-colors">
                  <g.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-heading font-bold text-navy text-lg mb-2 leading-snug">{g.title}</h3>
                <p className="text-sm text-foreground/75 leading-relaxed">{g.body}</p>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
      </div>
    </section>

    {/* WHO YOU TALK TO */}
    <section className="py-16 lg:py-24 bg-white">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10 items-center">
          <Reveal>
            <div className="aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-border shadow-lg bg-secondary">
              <img
                src="/team/franklin-sofi.jpg"
                alt={`${founder.name}, ${founder.credentials}`}
                width={640}
                height={800}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-3">
              Who you actually talk to
            </p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy leading-tight mb-4 [text-wrap:balance]">
              {founder.name}, {founder.credentials} — on your case from day one.
            </h2>
            <p className="text-foreground/85 leading-[1.75] text-[16px] mb-5">
              Most "tax relief" calls are routed through a commissioned closer who hands you off to a
              rotating bench of unlicensed "case managers." Here, the CPA who reads your notices on
              the first call is the same CPA who files your POA, runs your analysis, and signs the
              resolution proposal sent to the IRS.
            </p>
            <ul className="space-y-2.5 mb-6">
              {founder.credentialsList.map((c) => (
                <li key={c} className="flex gap-3 text-foreground/85">
                  <ShieldCheck className="w-4 h-4 text-gold shrink-0 mt-1" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/about/franklin-sofi"
              className="inline-flex items-center gap-1 font-semibold text-navy hover:text-gold"
            >
              Read Franklin's full bio <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>

    {/* WHAT GOES WRONG IF YOU WAIT */}
    <section className="py-16 lg:py-20 bg-navy-dark text-white">
      <div className="container mx-auto px-4 max-w-4xl">
        <Reveal>
          <div className="flex items-center gap-2 text-gold text-[11px] font-bold uppercase tracking-[0.18em] mb-3">
            <AlertTriangle className="w-4 h-4" />
            Why we move fast on Day 1
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold leading-tight mb-4 [text-wrap:balance]">
            Every week without a POA on file is a week the IRS keeps moving.
          </h2>
          <p className="text-white/80 leading-relaxed text-[17px] mb-6">
            Failure-to-pay penalties accrue at 0.5% per month and the failure-to-file penalty stacks at
            5% per month up to 25%. Once a Final Notice of Intent to Levy (LT11 / Letter 1058) is
            mailed, you have 30 days to file a Collection Due Process appeal before the IRS can levy
            wages, drain accounts, or seize refunds. The POA stops the clock on you and starts it on us.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href={`tel:${brand.phoneTel}`}
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-6 py-3 rounded transition-transform hover:scale-[1.03]"
            >
              <Phone className="w-5 h-5" /> Call {brand.phone}
            </a>
            <Link
              to="/resources/irs-notices"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white font-semibold px-6 py-3 rounded border border-white/20"
            >
              Look up your IRS notice <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    {/* FAQ */}
    <section className="py-16 lg:py-24 bg-white">
      <div className="container mx-auto px-4 max-w-3xl">
        <SectionHeading
          eyebrow="Common questions"
          title="What clients ask before they engage."
        />
        <RevealStagger className="space-y-4" stagger={60}>
          {faqs.map((f) => (
            <RevealItem key={f.q}>
              <details className="group bg-secondary/40 hover:bg-secondary/60 border border-border rounded-lg p-5 transition-colors">
                <summary className="font-heading font-bold text-navy text-lg cursor-pointer list-none flex items-start justify-between gap-4">
                  <span>{f.q}</span>
                  <span className="text-gold text-2xl leading-none transition-transform group-open:rotate-45 shrink-0">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-foreground/80 leading-[1.75]">{f.a}</p>
              </details>
            </RevealItem>
          ))}
        </RevealStagger>
      </div>
    </section>

    <CTASection />
  </SiteLayout>
);

export default HowItWorks;
